import { useEffect, useRef, useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ThunkDispatch } from '@reduxjs/toolkit'
import { Stage, Layer, Image as KonvaImage, Line } from 'react-konva'

import { itemRemovalActions, FurnitureMaskLine } from '../../reducers/itemRemoval'
import { cursorStyle } from '../../utils/cursorStyles'
import { getSquareLinePoints, getTriangleLinePoints } from '../../utils/geometryCalculations'
import { useFurnitureRemovalDrawingEvents } from './useFurnitureRemovalDrawingEvents'

/**
 * □/△の線をオフスクリーンCanvasに不透明な赤色で描画する
 * KonvaImage側でopacityを適用することで、ポリゴン重なり部分のアルファ重畳を防止する
 */
function drawPolygonLinesToCanvas(
  lines: FurnitureMaskLine[],
  width: number,
  height: number
): HTMLCanvasElement | null {
  const polygonLines = lines.filter(l => l.head === 'square' || l.head === 'triangle')
  if (width === 0 || height === 0 || polygonLines.length === 0) return null

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.fillStyle = 'rgb(255, 0, 0)'

  polygonLines.forEach(line => {
    if (line.points.length < 4) return

    if (line.head === 'square') {
      for (let i = 0; i < line.points.length - 2; i += 2) {
        const x1 = line.points[i], y1 = line.points[i + 1]
        const x2 = line.points[i + 2], y2 = line.points[i + 3]
        if (x2 === undefined) break
        const pts = getSquareLinePoints(x1, y1, x2, y2, line.headSize)
        ctx.beginPath()
        ctx.moveTo(pts[0], pts[1])
        for (let j = 2; j < pts.length; j += 2) ctx.lineTo(pts[j], pts[j + 1])
        ctx.closePath()
        ctx.fill()
      }
    } else if (line.head === 'triangle') {
      for (let i = 0; i < line.points.length - 2; i += 2) {
        const x1 = line.points[i], y1 = line.points[i + 1]
        const x2 = line.points[i + 2], y2 = line.points[i + 3]
        if (x2 === undefined) break
        const pts = getTriangleLinePoints(x1, y1, x2, y2, line.headSize)
        ctx.beginPath()
        ctx.moveTo(pts[0], pts[1])
        for (let j = 2; j < pts.length; j += 2) ctx.lineTo(pts[j], pts[j + 1])
        ctx.closePath()
        ctx.fill()
      }
    }
  })

  return canvas
}

/**
 * 家具部分消去用の描画コンポーネント
 * Konvaを使用してキャンバス上でマスク描画機能を提供
 */
const FurnitureRemovalDrawing = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
  const stageWrapRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<any>(null)

  const [stageWidth, setStageWidth] = useState(0)
  const [stageHeight, setStageHeight] = useState(0)
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null)

  const {
    furnitureMaskLines,
    tempFurnitureMaskLines,
    furnitureBrushHead,
    furnitureBrushSize,
    furnitureRemovalOriginalImageUrl,
    showFurnitureRemovalDrawing,
  } = useSelector((state: any) => state.itemRemoval)

  // 描画イベントフック
  const { handleMouseDown, handleMouseMove, handleMouseUp, showCursor, hideCursor } =
    useFurnitureRemovalDrawingEvents(stageWrapRef)

  // 画像の読み込み
  useEffect(() => {
    if (!furnitureRemovalOriginalImageUrl) return

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      setImageElement(img)
    }
    img.src = furnitureRemovalOriginalImageUrl
  }, [furnitureRemovalOriginalImageUrl])

  // ステージサイズの計算
  useEffect(() => {
    if (!stageWrapRef.current || !imageElement) return

    const maxStageHeight =
      window.innerHeight - 11 * parseInt(getComputedStyle(document.documentElement).fontSize)
    const maxStageWidth = stageWrapRef.current.clientWidth

    let newStageHeight = imageElement.height * (maxStageWidth / imageElement.width)
    let newStageWidth: number

    if (newStageHeight <= maxStageHeight) {
      newStageWidth = maxStageWidth
    } else {
      newStageHeight = maxStageHeight
      newStageWidth = imageElement.width * (maxStageHeight / imageElement.height)
    }

    setStageHeight(newStageHeight)
    setStageWidth(newStageWidth)
    stageWrapRef.current.style.height = newStageHeight + 'px'
  }, [imageElement])

  // stageRefをReduxに保存
  useEffect(() => {
    if (stageRef.current && showFurnitureRemovalDrawing) {
      const stage = stageRef.current.getStage()
      dispatch(itemRemovalActions.setFurnitureMaskStageRef(stage))
    }
  }, [stageRef.current, showFurnitureRemovalDrawing, dispatch])

  // □/△のオフスクリーンCanvas（確定済み）
  const confirmedPolygonCanvas = useMemo(
    () => drawPolygonLinesToCanvas(furnitureMaskLines, stageWidth, stageHeight),
    [furnitureMaskLines, stageWidth, stageHeight]
  )

  // □/△のオフスクリーンCanvas（描画中）
  const tempPolygonCanvas = useMemo(
    () => drawPolygonLinesToCanvas(tempFurnitureMaskLines, stageWidth, stageHeight),
    [tempFurnitureMaskLines, stageWidth, stageHeight]
  )

  return (
    <div ref={stageWrapRef} className="furniture-removal-drawing-wrapper relative flex justify-center">
      <Stage
        ref={stageRef}
        width={stageWidth}
        height={stageHeight}
        className="mx-auto"
        style={{ cursor: cursorStyle(furnitureBrushHead, furnitureBrushSize) }}
        onMouseEnter={showCursor}
        onMouseLeave={hideCursor}
        onMouseDown={handleMouseDown}
        onMousemove={handleMouseMove}
        onMouseup={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
      >
        {/* 背景画像レイヤー */}
        <Layer>
          {imageElement && (
            <KonvaImage
              image={imageElement}
              width={stageWidth}
              height={stageHeight}
            />
          )}
        </Layer>

        {/* マスク描画レイヤー */}
        <Layer listening={false}>
          {/* ○マーカー（確定済み）- 単一パスなのでアルファ重畳なし */}
          {furnitureMaskLines
            .filter((l: FurnitureMaskLine) => l.head === 'circle')
            .map((line: FurnitureMaskLine, i: number) => (
              <Line
                key={`confirmed-circle-${i}`}
                points={line.points}
                stroke="rgba(255, 0, 0, 0.6)"
                strokeWidth={line.headSize}
                tension={0.3}
                lineCap="round"
                lineJoin="round"
              />
            ))}

          {/* □/△マーカー（確定済み）- オフスクリーンCanvasで均一なopacityを実現 */}
          {confirmedPolygonCanvas && (
            <KonvaImage
              image={confirmedPolygonCanvas}
              width={stageWidth}
              height={stageHeight}
              opacity={0.6}
            />
          )}

          {/* ○マーカー（描画中） */}
          {tempFurnitureMaskLines
            .filter((l: FurnitureMaskLine) => l.head === 'circle')
            .map((line: FurnitureMaskLine, i: number) => (
              <Line
                key={`temp-circle-${i}`}
                points={line.points}
                stroke="rgba(255, 0, 0, 0.3)"
                strokeWidth={line.headSize}
                tension={0.3}
                lineCap="round"
                lineJoin="round"
              />
            ))}

          {/* □/△マーカー（描画中） */}
          {tempPolygonCanvas && (
            <KonvaImage
              image={tempPolygonCanvas}
              width={stageWidth}
              height={stageHeight}
              opacity={0.3}
            />
          )}
        </Layer>
      </Stage>
    </div>
  )
}

export default FurnitureRemovalDrawing
